import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ButtonComponent } from '@examdojo/core/button';
import { applySuperLatexActions, SuperLatexComponent, SuperLatexContent } from '@examdojo/super-latex';
import { KatexOptions, MarkdownModule } from 'ngx-markdown';
import { SimpleLayoutConfig, SolverSlide } from '../solver/solver.model';

@Component({
  selector: 'dojo-simple-solver',
  imports: [SuperLatexComponent, ButtonComponent, MarkdownModule],
  templateUrl: './simple-solver.component.html',
  styleUrl: './simple-solver.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class SimpleSolverComponent {
  private readonly elementRef = inject(ElementRef);
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  readonly katexOptions: KatexOptions = {
    throwOnError: false,
    strict: 'warn',
  };

  readonly slide = input.required<SolverSlide>();
  readonly config = input<SimpleLayoutConfig>();
  readonly resizable = input<boolean>(false);
  readonly widthChange = output<number>();

  readonly currentStepIndex = signal<number | null>(null);

  readonly stepsViewModel = computed(() => {
    const slide = this.slide();
    const initialLatex = slide.initial_super_latex_content;

    // Each step applies ONLY its own actions to the initial state (not cumulative)
    const processedSteps =
      slide.steps?.map((step) => {
        let computedContent = step.super_latex_actions
          ? applySuperLatexActions(initialLatex, step.super_latex_actions)
          : initialLatex;

        // Apply focus/dim transformations
        computedContent = this.applyFocusDim(computedContent, step.focus_anchors);

        return {
          ...step,
          computedContent,
        };
      }) ?? [];

    if (processedSteps.length === 0) {
      return [
        {
          title: slide.name || '',
          computedContent: initialLatex,
        },
      ];
    }

    return processedSteps;
  });

  readonly currentContent = computed(() => {
    const steps = this.stepsViewModel();
    const index = this.currentStepIndex();
    // When no step is selected (null), show initial content
    if (index === null) {
      return this.slide().initial_super_latex_content;
    }
    return steps[index]?.computedContent ?? this.slide().initial_super_latex_content;
  });

  @HostBinding('style.--latex-panel-width')
  get latexPanelWidth(): string | null {
    const width = this.config()?.width;
    return width ? `${width}px` : null;
  }

  @HostBinding('attr.tabindex')
  readonly tabindex = 0;

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.currentStepIndex.set(null);
      (document.activeElement as HTMLElement)?.blur();
      return;
    }

    const steps = this.stepsViewModel();
    if (steps.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const currentIndex = this.currentStepIndex();
      if (currentIndex === null) {
        this.currentStepIndex.set(0);
      } else if (currentIndex < steps.length - 1) {
        this.currentStepIndex.set(currentIndex + 1);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const currentIndex = this.currentStepIndex();
      if (currentIndex === null) {
        this.currentStepIndex.set(steps.length - 1);
      } else if (currentIndex > 0) {
        this.currentStepIndex.set(currentIndex - 1);
      }
    }
  }

  setStep(index: number): void {
    // Toggle off if clicking the same step
    if (this.currentStepIndex() === index) {
      this.currentStepIndex.set(null);
      // Blur any focused element to disable keyboard navigation
      (document.activeElement as HTMLElement)?.blur();
    } else {
      this.currentStepIndex.set(index);
      // Focus the host to enable keyboard navigation
      this.elementRef.nativeElement.focus();
    }
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.config()?.width ?? 350;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - this.resizeStartX;
      const newWidth = Math.max(200, Math.min(800, this.resizeStartWidth + deltaX));
      this.widthChange.emit(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  /**
   * Applies focus/dim transformations to SuperLatexContent.
   * - Passes focus_anchors to SuperLatex for color processing
   * - Nodes without any focus anchors are dimmed
   */
  private applyFocusDim(content: SuperLatexContent, focusAnchors?: string[]): SuperLatexContent {
    if (!focusAnchors?.length) {
      return content;
    }

    let result = { ...content, focus_anchors: focusAnchors };
    const focusSet = new Set(focusAnchors);
    const dimmedNodes: string[] = [];

    for (const node of content.latex_nodes) {
      const anchorMatches = node.latex.match(/anchor=([a-zA-Z0-9_-]+)/g);
      if (!anchorMatches) {
        // No anchors = dim the node
        if (node.id) dimmedNodes.push(node.id);
        continue;
      }

      const nodeAnchors = anchorMatches.map((m) => m.replace('anchor=', ''));

      // Check if any of the node's anchors are in the focus set
      const hasFocusAnchor = nodeAnchors.some((a) => focusSet.has(a));
      if (!hasFocusAnchor && node.id) {
        dimmedNodes.push(node.id);
      }
    }

    if (dimmedNodes.length > 0) {
      result = { ...result, dimmed_nodes: dimmedNodes };
    }

    return result;
  }
}
